// OPTION_B = unlimited free transfers, no penalty — default
// OPTION_A = 3 free transfers per GW, −5 pts per extra transfer
export const LeagueTransferOption = {
  OptionB: 'OPTION_B',
  OptionA: 'OPTION_A',
} as const
export type LeagueTransferOption = (typeof LeagueTransferOption)[keyof typeof LeagueTransferOption]

export const LeagueScoringSystem = {
  NossaPreset: 'NOSSA_PRESET',
} as const
export type LeagueScoringSystem = (typeof LeagueScoringSystem)[keyof typeof LeagueScoringSystem]

export const LeagueFormat = {
  OverallPoints: 'OVERALL_POINTS',
  HeadToHead: 'HEAD_TO_HEAD',
} as const
export type LeagueFormat = (typeof LeagueFormat)[keyof typeof LeagueFormat]

export type LeagueFormValues = {
  name: string
  teamName: string
  competitionId: string
  scoringSystem: LeagueScoringSystem
  format: LeagueFormat
  transferOption: LeagueTransferOption
}

// ===== GAMEWEEK / WINDOW STATE =====

export const TransferWindowState = {
  Open: 'open',
  Closed: 'closed',
} as const
export type TransferWindowState = (typeof TransferWindowState)[keyof typeof TransferWindowState]

export type GameweekStatus = {
  currentGameweek: number | null   // null = no GW has started yet
  windowState: TransferWindowState
  windowChangesAt: string | null   // ISO — when window flips state next
  scoresFinalized: boolean         // whether current GW scores are final
}

// ===== LEAGUE =====

export type League = {
  id: string
  name: string
  format: LeagueFormat
  transferOption: LeagueTransferOption
  scoringSystem: LeagueScoringSystem
  joinCode: string
  memberCount: number
  maxParticipants: number
  isAdmin: boolean
  isLocked: boolean   // true once the league's first gameweek has locked
  gwStatus: GameweekStatus
}

// ===== STANDINGS =====

export type StandingsEntry = {
  rank: number
  previousRank: number | null   // null = first GW, no delta yet
  userId: string
  squadId: string
  teamName: string
  gwPoints: number
  totalPoints: number
  transfersUsed: number
  isCurrentUser: boolean
  isAverageParticipant: boolean  // H2H odd-number ghost entry
  hasSquad: boolean
  // H2H only
  h2hPoints?: number
  wins?: number
  draws?: number
  losses?: number
}

// ===== MEMBERS =====

export type LeagueMember = {
  userId: string
  teamName: string | null   // null = no squad built yet
  isAdmin: boolean
  isCurrentUser: boolean
  joinedAt: string
}

// ===== H2H FIXTURES =====

export const FixtureStatus = {
  Upcoming: 'upcoming',
  Live: 'live',
  Finished: 'finished',
} as const
export type FixtureStatus = (typeof FixtureStatus)[keyof typeof FixtureStatus]

export type H2HFixture = {
  gameweek: number
  homeUserId: string
  homeTeamName: string
  homePoints: number | null
  awayUserId: string | null   // null = "Média" (average/bye) participant
  awayTeamName: string
  awayPoints: number | null
  status: FixtureStatus
  isCurrentUserMatch: boolean
}

// ===== COMPETITION STATUS =====

export const CompetitionStatus = {
  NotStarted: 'NOT_STARTED',
  InProgress: 'IN_PROGRESS',
  Finished:   'FINISHED',
} as const
export type CompetitionStatus = (typeof CompetitionStatus)[keyof typeof CompetitionStatus]

// ===== LEAGUE SUMMARY (card in "Suas Ligas" list) =====

export type SquadPreviewPlayer = {
  position: SquadPosition
  name:     string
  isStarter: boolean
}

export type LeagueSummary = {
  id: string
  name: string
  format: LeagueFormat
  joinCode: string
  isPaidLeague: boolean
  isFavorite: boolean
  competitionId: string
  competitionName: string
  competitionSport: 'FOOTBALL' | 'CS2'
  competitionStatus: CompetitionStatus  // "NOT_STARTED" | "IN_PROGRESS" | "FINISHED"
  rank: number | null                   // null pre-competition (no GW finalized yet)
  currentGameweek: number | null
  gwPoints: number
  totalPoints: number
  memberCount: number
  h2hPoints:      number | null          // null for OVERALL_POINTS leagues
  pointsGapAbove: number | null          // positive = lead (rank 1), negative = deficit; null pre-comp
  squadPreview:   SquadPreviewPlayer[]   // empty array if no squad built
  maxParticipants: number
  windowState: TransferWindowState
  windowChangesAt: string | null        // ISO UTC — null if no change scheduled
  nextGameweekStartsAt: string | null   // ISO UTC — null if no GW scheduled
  squadUpdatedAt: string | null         // ISO UTC — when the squad was last saved; null if no squad built yet
}

// ===== SQUAD (team preview — matches GET /v1/leagues/{leagueId}/my-squad) =====

export const SquadPosition = {
  GK:  'GK',
  DEF: 'DEF',
  MID: 'MID',
  FWD: 'FWD',
} as const
export type SquadPosition = (typeof SquadPosition)[keyof typeof SquadPosition]

export type SquadPlayer = {
  id: string
  name: string
  position: SquadPosition
  teamCode: string
  price: number
  isEliminated: boolean
}

export type SquadEntry = {
  slot: string  // 'STARTER_GK' | 'STARTER_1'..'STARTER_10' | 'BENCH_GK' | 'BENCH_1'..'BENCH_3'
  player: SquadPlayer
}

export type Squad = {
  squadId: string
  leagueId: string
  starters: SquadEntry[]
  bench: SquadEntry[]
  formation: string     // e.g. '4-3-3'
  totalValue: number
  remainingBudget: number
}

// ===== EVENTS FEED =====

export const LeagueEventType = {
  RankChange: 'rank_change',
  AdminAction: 'admin_action',
  Banter: 'banter',
} as const
export type LeagueEventType = (typeof LeagueEventType)[keyof typeof LeagueEventType]

export type LeagueEvent = {
  id: string
  type: LeagueEventType
  userId: string | null
  teamName: string | null
  content: string
  timestamp: string
}
